import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  CalendarDays,
  MoreHorizontal,
  Edit,
  Trash2,
  User,
  Scissors,
  Euro,
  Phone,
  Mail,
  AlertCircle,
  Search,
  UserPlus,
  Users,
  Megaphone,
  Ban,
  RotateCcw,
  UserX,
  Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay, parseISO, addMinutes } from 'date-fns';
import { it } from 'date-fns/locale';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import SlotBroadcastDialog from '../components/SlotBroadcastDialog';
import PendingReservationsBanner from '../components/PendingReservationsBanner';
import { DayGrid } from '../components/calendar/DayGrid';

const CalendarPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('date');
    const d = q ? new Date(`${q}T12:00:00`) : null;
    return d && !Number.isNaN(d.getTime()) ? d : new Date();
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedStaffFilter, setSelectedStaffFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [mobileSelectedDay, setMobileSelectedDay] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('date');
    const d = q ? new Date(`${q}T12:00:00`) : null;
    return d && !Number.isNaN(d.getTime()) ? d : new Date();
  });
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [broadcastSlot, setBroadcastSlot] = useState(null); // { datetime, duration_min, service_name, appointment_id }
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('calendar_view') || 'day');
  const [dayHours, setDayHours] = useState({ open: '09:00', close: '19:00', closed: false });
  const [busySlots, setBusySlots] = useState([]);
  
  // Form state - Treatwell style
  const [formData, setFormData] = useState({
    service_id: '',
    staff_id: '',
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    start_time: '',
    end_time: '',
    appointment_status: 'scheduled',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [appointmentsRes, servicesRes, staffRes, customersRes] = await Promise.all([
        axios.get(`${API}/appointments`, getAuthHeaders()),
        axios.get(`${API}/services`, getAuthHeaders()),
        axios.get(`${API}/staff`, getAuthHeaders()),
        axios.get(`${API}/customers`, getAuthHeaders())
      ]);
      setAppointments(appointmentsRes.data);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Orari di apertura del giorno mostrato: la griglia parte e finisce lì
  useEffect(() => {
    if (viewMode !== 'day') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/calendar/day-grid?date=${format(currentDate, 'yyyy-MM-dd')}`, getAuthHeaders());
        if (!cancelled) setDayHours(res.data);
      } catch {
        if (!cancelled) setDayHours({ open: '09:00', close: '19:00', closed: false });
      }
      try {
        const res = await axios.get(
          `${API}/calendar/busy?date=${format(currentDate, 'yyyy-MM-dd')}`, getAuthHeaders());
        if (!cancelled) setBusySlots(res.data.busy || []);
      } catch {
        if (!cancelled) setBusySlots([]);
      }
    })();
    return () => { cancelled = true; };
  }, [API, getAuthHeaders, currentDate, viewMode]);

  const changeView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('calendar_view', mode);
  };

  const openSlot = (staffId, startDate) => {
    setSelectedAppointment(null);
    setSelectedCustomerId(null);
    setCustomerSearch('');
    setShowCustomerSearch(false);
    setFormData({
      service_id: '', staff_id: staffId, customer_id: '', customer_name: '',
      customer_phone: '', customer_email: '',
      start_time: startDate.toISOString(), end_time: '',
      appointment_status: 'scheduled', notes: '',
    });
    setIsDialogOpen(true);
  };

  const moveAppointment = async (appointment, staffId, newStart, durationMin) => {
    const end = addMinutes(newStart, durationMin);
    try {
      await axios.patch(`${API}/appointments/${appointment.id}/move`, {
        start_time: newStart.toISOString(),
        end_time: end.toISOString(),
        staff_id: staffId,
      }, getAuthHeaders());
      toast.success(`Spostato alle ${format(newStart, 'HH:mm')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Spostamento non riuscito');
    }
  };

  // Auto-open New Appointment dialog when navigated from Inbox with query params
  // Example: /calendar?newAppointment=1&phone=...&name=...&email=...&service=Taglio
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    if (params.get('newAppointment') !== '1') return;

    const phone = params.get('phone') || '';
    const name = params.get('name') || '';
    const email = params.get('email') || '';
    const serviceHint = (params.get('service') || '').trim().toLowerCase();

    // Try to find an existing customer by phone (last 9 digits match) to link CRM record
    let matchedCustomer = null;
    if (phone) {
      const norm = phone.replace(/[^0-9]/g, '').slice(-9);
      matchedCustomer = customers.find((c) => (c.phone || '').replace(/[^0-9]/g, '').slice(-9) === norm) || null;
    }

    // Try to match service by name (case-insensitive partial)
    let matchedServiceId = '';
    if (serviceHint && services.length) {
      const svc = services.find((s) => (s.name || '').toLowerCase() === serviceHint)
        || services.find((s) => (s.name || '').toLowerCase().includes(serviceHint))
        || services.find((s) => serviceHint.includes((s.name || '').toLowerCase()));
      if (svc) matchedServiceId = svc.id;
    }

    // Default start time: next half-hour today
    const now = new Date();
    const start = new Date(now);
    start.setMinutes(now.getMinutes() < 30 ? 30 : 60, 0, 0);

    setFormData({
      service_id: matchedServiceId,
      staff_id: '',
      customer_id: matchedCustomer?.id || '',
      customer_name: matchedCustomer?.name || name,
      customer_phone: matchedCustomer?.phone || phone,
      customer_email: matchedCustomer?.email || email,
      start_time: start.toISOString(),
      end_time: '',
      appointment_status: 'scheduled',
      notes: '',
    });
    if (matchedCustomer) setSelectedCustomerId(matchedCustomer.id);
    setSelectedAppointment(null);
    setIsDialogOpen(true);

    // Clean the URL so the dialog doesn't reopen on refresh / re-render
    navigate('/calendar', { replace: true });
  }, [loading, location.search, customers, services, navigate]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service_id || !formData.staff_id) {
      alert('Seleziona un servizio e un operatore');
      return;
    }
    try {
      if (selectedAppointment) {
        await axios.put(`${API}/appointments/${selectedAppointment.id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${API}/appointments`, formData, getAuthHeaders());
      }
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Errore nel salvataggio appuntamento:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo appuntamento?')) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, getAuthHeaders());
      fetchData();
    } catch (error) {
      console.error('Errore nell\'eliminazione appuntamento:', error);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancellare questo appuntamento? Lo slot verrà segnato come libero ma l\'appuntamento resterà visibile in agenda.')) return;
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { appointment_status: 'cancelled' }, getAuthHeaders());
      fetchData();
    } catch (error) {
      console.error('Errore nella cancellazione:', error);
    }
  };

  const handleNoShow = async (id) => {
    if (!window.confirm('Segnare il cliente come non presentato? Se aveva versato un acconto, resta al salone.')) return;
    try {
      const res = await axios.patch(`${API}/appointments/${id}/status`, { appointment_status: 'no_show' }, getAuthHeaders());
      if (res.data.deposit_outcome === 'kept') toast.success('Acconto trattenuto');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operazione non riuscita');
    }
  };

  const handleRefundDeposit = async (id) => {
    if (!window.confirm('Restituire l\'acconto al cliente? Il rimborso parte subito sulla sua carta.')) return;
    try {
      const res = await axios.post(`${API}/payments/deposit/${id}/refund`, {}, getAuthHeaders());
      toast.success(`Acconto di €${res.data.amount.toFixed(2)} restituito`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rimborso non riuscito');
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { appointment_status: 'scheduled' }, getAuthHeaders());
      fetchData();
    } catch (error) {
      console.error('Errore nel ripristino:', error);
    }
  };

  const openBroadcastForAppointment = (apt) => {
    const start = new Date(apt.start_time);
    const end = apt.end_time ? new Date(apt.end_time) : null;
    const duration = end ? Math.round((end - start) / 60000) : 30;
    setBroadcastSlot({
      datetime: apt.start_time,
      duration_min: duration,
      service_name: apt.service_name,
      appointment_id: apt.id,
    });
    setBroadcastDialogOpen(true);
  };

  const onBroadcastSent = async (response) => {
    setBroadcastSlot(null);
    fetchData();
  };

  const resetForm = () => {
    setFormData({
      service_id: '',
      staff_id: '',
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      start_time: '',
      end_time: '',
      appointment_status: 'scheduled',
      notes: ''
    });
    setSelectedAppointment(null);
    setSelectedCustomerId(null);
    setCustomerSearch('');
    setShowCustomerSearch(false);
    setIsDialogOpen(false);
  };

  const openNewAppointment = (date, hour = 9) => {
    const startTime = new Date(date);
    startTime.setHours(hour, 0, 0, 0);
    
    setFormData({
      service_id: '',
      staff_id: '',
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      start_time: startTime.toISOString(),
      end_time: '',
      appointment_status: 'scheduled',
      notes: ''
    });
    setSelectedCustomerId(null);
    setCustomerSearch('');
    setShowCustomerSearch(false);
    setIsDialogOpen(true);
  };

  const openEdit = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      service_id: appointment.service_id,
      staff_id: appointment.staff_id,
      customer_id: appointment.customer_id || '',
      customer_name: appointment.customer_name || '',
      customer_phone: appointment.customer_phone || '',
      customer_email: appointment.customer_email || '',
      start_time: appointment.start_time,
      end_time: appointment.end_time,
      appointment_status: appointment.appointment_status,
      notes: appointment.notes || ''
    });
    setSelectedCustomerId(appointment.customer_id || null);
    setCustomerSearch('');
    setShowCustomerSearch(false);
    setIsDialogOpen(true);
  };

  // When service is selected, auto-calculate end time
  const handleServiceChange = (serviceId) => {
    const selectedService = services.find(s => s.id === serviceId);
    setFormData(prev => {
      const newData = { ...prev, service_id: serviceId };
      if (selectedService && prev.start_time) {
        const startDate = new Date(prev.start_time);
        const endDate = addMinutes(startDate, selectedService.duration);
        newData.end_time = endDate.toISOString();
      }
      return newData;
    });
  };

  // Select customer from CRM
  const handleSelectCustomer = (customer) => {
    setSelectedCustomerId(customer.id);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_phone: customer.phone || '',
      customer_email: customer.email || ''
    }));
    setCustomerSearch('');
    setShowCustomerSearch(false);
  };

  // Clear selected customer
  const handleClearCustomer = () => {
    setSelectedCustomerId(null);
    setFormData(prev => ({
      ...prev,
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: ''
    }));
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (c.email && c.email.toLowerCase().includes(customerSearch.toLowerCase())) ||
    (c.phone && c.phone.includes(customerSearch))
  ).slice(0, 5);

  // Filter staff based on selected service
  const getAvailableStaff = () => {
    if (!formData.service_id) return staff.filter(s => s.is_active);
    return staff.filter(s => s.is_active && s.services.includes(formData.service_id));
  };

  const getWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  const getAppointmentsForDay = (date) => {
    return appointments.filter(apt => {
      const aptDate = parseISO(apt.start_time);
      const matchesDay = isSameDay(aptDate, date);
      const matchesStaff = selectedStaffFilter === 'all' || apt.staff_id === selectedStaffFilter;
      return matchesDay && matchesStaff;
    });
  };

  const getStaffColor = useCallback((staffId) => {
    const member = staff.find(s => s.id === staffId);
    return member?.avatar_color || '#6366F1';
  }, [staff]);

  const statusColors = {
    scheduled: 'border-l-blue-500',
    completed: 'border-l-emerald-500',
    cancelled: 'border-l-red-500',
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);
  const activeServices = useMemo(() => services.filter(s => s.is_active), [services]);
  const activeStaff = useMemo(() => staff.filter(s => s.is_active), [staff]);

  const upcomingAppointments = useMemo(() => {
    const now = new Date();
    return appointments
      .filter(apt => new Date(apt.start_time) >= now && apt.appointment_status !== 'completed')
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .slice(0, 8);
  }, [appointments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check if services and staff are configured
  const isConfigured = activeServices.length > 0 && activeStaff.length > 0;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Calendario</h1>
          <p className="text-muted-foreground mt-1">Gestisci i tuoi appuntamenti</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          {/* Staff Filter */}
          <Select value={selectedStaffFilter} onValueChange={setSelectedStaffFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="staff-filter">
              <SelectValue placeholder="Filtra per operatore" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli operatori</SelectItem>
              {activeStaff.map(member => (
                <SelectItem key={member.id} value={member.id}>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: member.avatar_color || '#6366F1' }}
                    />
                    {member.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-appointment-btn" disabled={!isConfigured}>
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Appuntamento
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
              <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                <DialogTitle className="font-outfit">
                  {selectedAppointment ? 'Modifica Appuntamento' : 'Nuovo Appuntamento'}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <form onSubmit={handleSubmit} className="space-y-5 mt-2">
                  {/* Service Selection - Treatwell Style */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Scissors className="w-4 h-4" />
                      Seleziona Servizio
                    </Label>
                    <div className="grid gap-2">
                      {activeServices.map((service) => (
                        <div
                          key={service.id}
                          onClick={() => handleServiceChange(service.id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            formData.service_id === service.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`service-${service.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{service.name}</p>
                              {service.description && (
                                <p className="text-sm text-muted-foreground">{service.description}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-primary">€{service.price.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {service.duration} min
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {activeServices.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Nessun servizio configurato. Vai su Impostazioni per aggiungere servizi.
                      </p>
                    )}
                  </div>

                  {/* Staff Selection */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Seleziona Operatore
                    </Label>
                    <div className="grid gap-2">
                      {getAvailableStaff().map((member) => (
                        <div
                          key={member.id}
                          onClick={() => setFormData({...formData, staff_id: member.id})}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-3 ${
                            formData.staff_id === member.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`staff-${member.id}`}
                        >
                          <Avatar className="w-10 h-10">
                            <AvatarFallback style={{ backgroundColor: member.avatar_color || '#6366F1' }} className="text-white">
                              {member.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{member.role}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {formData.service_id && getAvailableStaff().length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Nessun operatore disponibile per questo servizio.
                      </p>
                    )}
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data e Ora</Label>
                      <Input
                        type="datetime-local"
                        value={formData.start_time ? formData.start_time.slice(0, 16) : ''}
                        onChange={(e) => {
                          const startDate = new Date(e.target.value);
                          const selectedService = services.find(s => s.id === formData.service_id);
                          const endDate = selectedService 
                            ? addMinutes(startDate, selectedService.duration)
                            : addMinutes(startDate, 30);
                          setFormData({
                            ...formData, 
                            start_time: startDate.toISOString(),
                            end_time: endDate.toISOString()
                          });
                        }}
                        required
                        data-testid="appointment-datetime-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Durata</Label>
                      <div className="h-10 px-3 flex items-center bg-muted rounded-md text-sm">
                        {formData.service_id 
                          ? `${services.find(s => s.id === formData.service_id)?.duration || 30} minuti`
                          : 'Seleziona servizio'
                        }
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="space-y-3 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Cliente
                      </Label>
                      {customers.length > 0 && !selectedCustomerId && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                          data-testid="toggle-customer-search"
                        >
                          <Search className="w-3 h-3 mr-1" />
                          Cerca nel CRM
                        </Button>
                      )}
                    </div>

                    {/* Customer Search from CRM */}
                    {showCustomerSearch && !selectedCustomerId && (
                      <div className="space-y-2 p-3 bg-muted/50 rounded-lg border">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            placeholder="Cerca cliente per nome, email o telefono..."
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            className="pl-9"
                            data-testid="customer-search-input"
                          />
                        </div>
                        {customerSearch && filteredCustomers.length > 0 && (
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                onClick={() => handleSelectCustomer(customer)}
                                className="p-2 rounded-md hover:bg-primary/10 cursor-pointer flex items-center justify-between"
                                data-testid={`customer-option-${customer.id}`}
                              >
                                <div>
                                  <p className="font-medium text-sm">{customer.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {customer.email || customer.phone || 'Nessun contatto'}
                                  </p>
                                </div>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {customer.customer_status === 'customer' ? 'Cliente' : 'Lead'}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                        {customerSearch && filteredCustomers.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            Nessun cliente trovato
                          </p>
                        )}
                        {!customerSearch && (
                          <p className="text-xs text-muted-foreground text-center">
                            Digita per cercare tra {customers.length} clienti
                          </p>
                        )}
                      </div>
                    )}

                    {/* Selected Customer Display */}
                    {selectedCustomerId && (
                      <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-primary/20 text-primary">
                              {formData.customer_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{formData.customer_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formData.customer_email || formData.customer_phone || 'Cliente CRM'}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleClearCustomer}
                          data-testid="clear-customer-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {/* Manual Customer Entry */}
                    {!selectedCustomerId && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <UserPlus className="w-3 h-3" />
                          {customers.length > 0 ? 'Oppure inserisci manualmente:' : 'Inserisci i dati cliente:'}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="customer_name">Nome Cliente</Label>
                          <Input
                            id="customer_name"
                            value={formData.customer_name}
                            onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                            placeholder="Mario Rossi"
                            data-testid="customer-name-input"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="customer_phone">Telefono</Label>
                            <Input
                              id="customer_phone"
                              value={formData.customer_phone}
                              onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                              placeholder="+39 333 1234567"
                              data-testid="customer-phone-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="customer_email">Email</Label>
                            <Input
                              id="customer_email"
                              type="email"
                              value={formData.customer_email}
                              onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                              placeholder="email@esempio.it"
                              data-testid="customer-email-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notes - always visible */}
                    <div className="space-y-2">
                      <Label htmlFor="notes">Note</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        placeholder="Note aggiuntive..."
                        rows={2}
                        data-testid="appointment-notes-input"
                      />
                    </div>
                  </div>

                  <div className="pt-4 space-y-2">
                    {selectedAppointment && formData.appointment_status === 'cancelled' && formData.start_time && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setBroadcastDialogOpen(true)}
                        className="w-full"
                        data-testid="open-broadcast-slot-btn"
                      >
                        <Megaphone className="w-4 h-4 mr-2" />
                        Avvisa Clienti dello Slot Libero
                      </Button>
                    )}
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                      <Button type="button" variant="outline" onClick={resetForm}>Annulla</Button>
                      <Button 
                        type="submit" 
                        disabled={!formData.service_id || !formData.staff_id || !formData.start_time}
                        data-testid="save-appointment-btn"
                      >
                        {selectedAppointment ? 'Aggiorna' : 'Prenota'} Appuntamento
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Warning if not configured */}
      {!isConfigured && (
        <Card className="border-orange-500/50 bg-orange-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Configurazione necessaria</p>
              <p className="text-sm text-muted-foreground">
                Per creare appuntamenti, devi prima configurare servizi e operatori. 
                Vai su <a href="/settings" className="text-primary hover:underline">Impostazioni → Servizi & Staff</a>.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pre-booked slots awaiting salon confirmation */}
      <PendingReservationsBanner
        services={services}
        staff={staff}
        onConfirmed={fetchData}
      />

      {/* Calendar Navigation */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Button 
                variant="outline" 
                size="icon"
                className="h-9 w-9"
                onClick={() => setCurrentDate(viewMode === 'day' ? addDays(currentDate, -1) : subWeeks(currentDate, 1))}
                data-testid="prev-week"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                className="h-9 w-9"
                onClick={() => setCurrentDate(viewMode === 'day' ? addDays(currentDate, 1) : addWeeks(currentDate, 1))}
                data-testid="next-week"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => { setCurrentDate(new Date()); setMobileSelectedDay(new Date()); }}
                data-testid="today-btn"
              >
                Oggi
              </Button>
            </div>
            <h2 className="order-last w-full text-center text-sm font-semibold font-outfit truncate px-1 sm:order-none sm:w-auto sm:flex-1 sm:text-lg">
              {viewMode === 'day' ? (
                <span data-testid="calendar-range-day">{format(currentDate, "EEEE d MMMM", { locale: it })}</span>
              ) : (
                <>
                  <span className="hidden sm:inline">
                    {format(weekDays[0], 'd MMM', { locale: it })} - {format(weekDays[6], 'd MMM yyyy', { locale: it })}
                  </span>
                  <span className="sm:hidden">
                    {format(weekDays[0], 'd')} - {format(weekDays[6], 'd MMM', { locale: it })}
                  </span>
                </>
              )}
            </h2>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="flex rounded-lg border p-0.5" data-testid="calendar-view-switch">
                {[['day', 'Giorno'], ['week', 'Settimana']].map(([mode, text]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => changeView(mode)}
                    className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors sm:px-3 ${
                      viewMode === mode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`calendar-view-${mode}`}
                  >
                    {text}
                  </button>
                ))}
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" data-testid="date-picker-trigger">
                    <CalendarDays className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={currentDate}
                    onSelect={(date) => { if (date) { setCurrentDate(date); setMobileSelectedDay(date); } }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {viewMode === 'day' && (
        <DayGrid
          date={currentDate}
          staff={selectedStaffFilter === 'all' ? activeStaff : activeStaff.filter(s => s.id === selectedStaffFilter)}
          appointments={getAppointmentsForDay(currentDate)}
          openTime={dayHours.open}
          closeTime={dayHours.close}
          closed={dayHours.closed}
          closedReason={dayHours.closed_reason}
          busy={busySlots}
          getStaffColor={getStaffColor}
          onSlotClick={openSlot}
          onAppointmentClick={openEdit}
          onMove={moveAppointment}
        />
      )}

      {viewMode === 'week' && (
      <>
      {/* Week View - Desktop (md+) */}
      <div className="hidden md:grid grid-cols-7 gap-4">
        {weekDays.map((day) => {
          const dayAppointments = getAppointmentsForDay(day);
          const isToday = isSameDay(day, new Date());
          const dayKey = format(day, 'yyyy-MM-dd');
          
          return (
            <Card 
              key={dayKey} 
              className={`min-h-[350px] ${isToday ? 'ring-2 ring-primary' : ''}`}
              data-testid={`day-${dayKey}`}
            >
              <CardHeader className="pb-2 pt-3 px-3">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">{format(day, 'EEE', { locale: it })}</p>
                  <p className={`text-xl font-bold font-outfit ${isToday ? 'text-primary' : ''}`}>
                    {format(day, 'd')}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-2 space-y-2">
                <ScrollArea className="h-[250px]">
                  <div className="space-y-2 pr-2">
                    {dayAppointments
                      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                      .map((apt) => {
                        const isCancelled = apt.appointment_status === 'cancelled';
                        return (
                        <div
                          key={apt.id}
                          className={`p-2 rounded-md text-xs cursor-pointer transition-all hover:opacity-90 border-l-4 shadow-sm ${
                            isCancelled
                              ? 'bg-red-50/40 dark:bg-red-950/20 border-dashed opacity-70'
                              : `bg-card ${statusColors[apt.appointment_status]}`
                          }`}
                          style={{ borderLeftColor: isCancelled ? undefined : getStaffColor(apt.staff_id) }}
                          onClick={() => openEdit(apt)}
                          data-testid={`appointment-${apt.id}`}
                        >
                          <p className={`font-semibold truncate ${isCancelled ? 'line-through text-muted-foreground' : ''}`}>{apt.service_name}</p>
                          <p className={`text-muted-foreground truncate ${isCancelled ? 'line-through' : ''}`}>{apt.customer_name || 'Cliente'}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-muted-foreground">
                              {isCancelled ? <span className="text-[9px] font-bold uppercase text-red-600 dark:text-red-400">Annullato</span> : format(parseISO(apt.start_time), 'HH:mm')}
                            </span>
                            <span className="text-[10px] truncate max-w-[60px]" title={apt.staff_name}>
                              {apt.staff_name?.split(' ')[0]}
                            </span>
                          </div>
                        </div>
                        );
                      })}
                  </div>
                </ScrollArea>
                {isConfigured && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full h-8 text-xs"
                    onClick={() => openNewAppointment(day)}
                    data-testid={`add-apt-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Aggiungi
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Week View - Mobile (< md): day tab strip + agenda list */}
      <div className="md:hidden space-y-3" data-testid="mobile-calendar-view">
        {/* Day tab strip */}
        <div className="grid grid-cols-7 gap-1" data-testid="mobile-day-strip">
          {weekDays.map((day) => {
            const dayKey = format(day, 'yyyy-MM-dd');
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, mobileSelectedDay);
            const dayAppointments = getAppointmentsForDay(day);
            const dotCount = Math.min(dayAppointments.length, 3);
            return (
              <button
                key={dayKey}
                onClick={() => setMobileSelectedDay(day)}
                className={`flex flex-col items-center py-2 px-0.5 rounded-lg border transition-all ${
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : isToday
                      ? 'border-primary/60 bg-primary/5 text-foreground'
                      : 'border-border bg-card hover:bg-muted text-foreground'
                }`}
                data-testid={`mobile-day-tab-${dayKey}`}
              >
                <span className={`text-[10px] uppercase font-medium ${isSelected ? 'opacity-90' : 'text-muted-foreground'}`}>
                  {format(day, 'EEE', { locale: it })}
                </span>
                <span className="text-lg font-bold font-outfit leading-tight mt-0.5">
                  {format(day, 'd')}
                </span>
                {dotCount > 0 && (
                  <div className="flex gap-0.5 mt-1 h-1.5">
                    {Array.from({ length: dotCount }).map((_, i) => (
                      <span
                        key={i}
                        className={`w-1 h-1 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-primary'}`}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected day agenda */}
        <Card data-testid="mobile-day-agenda">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">
                  {format(mobileSelectedDay, 'EEEE', { locale: it })}
                </p>
                <CardTitle className="font-outfit text-lg">
                  {format(mobileSelectedDay, "d MMMM yyyy", { locale: it })}
                </CardTitle>
              </div>
              {isConfigured && (
                <Button
                  size="sm"
                  onClick={() => openNewAppointment(mobileSelectedDay)}
                  data-testid={`mobile-add-apt-${format(mobileSelectedDay, 'yyyy-MM-dd')}`}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nuovo
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {(() => {
              const dayAppts = getAppointmentsForDay(mobileSelectedDay)
                .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
              if (dayAppts.length === 0) {
                return (
                  <div className="text-center py-10 text-muted-foreground">
                    <CalendarDays className="w-10 h-10 mx-auto opacity-40 mb-2" />
                    <p className="text-sm">Nessun appuntamento</p>
                  </div>
                );
              }
              return (
                <div className="space-y-2">
                  {dayAppts.map(apt => {
                    const isCancelled = apt.appointment_status === 'cancelled';
                    return (
                    <div
                      key={apt.id}
                      onClick={() => openEdit(apt)}
                      className={`flex gap-3 p-3 rounded-lg transition-colors cursor-pointer border-l-4 ${
                        isCancelled
                          ? 'bg-red-50/40 dark:bg-red-950/20 border-dashed opacity-70 hover:opacity-100'
                          : `bg-muted/40 hover:bg-muted ${statusColors[apt.appointment_status]}`
                      }`}
                      style={{ borderLeftColor: isCancelled ? undefined : getStaffColor(apt.staff_id) }}
                      data-testid={`mobile-appointment-${apt.id}`}
                    >
                      <div className="flex flex-col items-center justify-center min-w-[52px] text-center">
                        <p className={`font-bold text-sm font-outfit ${isCancelled ? 'line-through text-muted-foreground' : ''}`}>
                          {format(parseISO(apt.start_time), 'HH:mm')}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {format(parseISO(apt.end_time), 'HH:mm')}
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold text-sm truncate ${isCancelled ? 'line-through text-muted-foreground' : ''}`}>{apt.service_name}</p>
                          {isCancelled && (
                            <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-400">
                              Annullato
                            </span>
                          )}
                        </div>
                        <p className={`text-xs text-muted-foreground truncate ${isCancelled ? 'line-through' : ''}`}>
                          {apt.customer_name || 'Cliente'}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: getStaffColor(apt.staff_id) }}
                          />
                          <span className="text-[10px] text-muted-foreground truncate">
                            {apt.staff_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Upcoming Appointments */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit">Prossimi Appuntamenti</CardTitle>
        </CardHeader>
        <CardContent>
          {appointments.length > 0 ? (
            <div className="space-y-3">
              {upcomingAppointments.map((apt) => {
                const isCancelled = apt.appointment_status === 'cancelled';
                return (
                  <div 
                    key={apt.id}
                    className={`flex items-center justify-between gap-2 p-3 sm:p-4 rounded-lg transition-colors ${
                      isCancelled
                        ? 'bg-red-50/50 dark:bg-red-950/20 border border-dashed border-red-300 dark:border-red-900'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                    data-testid={`upcoming-appt-${apt.id}`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      <div 
                        className={`w-1.5 h-12 sm:h-14 rounded-full flex-shrink-0 ${isCancelled ? 'opacity-30' : ''}`}
                        style={{ backgroundColor: getStaffColor(apt.staff_id) }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold text-sm sm:text-base truncate ${
                            isCancelled ? 'line-through text-muted-foreground' : ''
                          }`}>{apt.service_name}</p>
                          {isCancelled && (
                            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-400">
                              Annullato
                            </span>
                          )}
                          {apt.appointment_status === 'no_show' && (
                            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400">
                              Non presentato
                            </span>
                          )}
                          {apt.deposit_paid && (
                            <span data-testid={`deposit-badge-${apt.id}`}
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                              {apt.deposit_outcome === 'refunded'
                                ? `acconto €${Number(apt.deposit_amount).toFixed(2)} restituito`
                                : apt.deposit_outcome === 'kept'
                                  ? `acconto €${Number(apt.deposit_amount).toFixed(2)} trattenuto`
                                  : `acconto €${Number(apt.deposit_amount).toFixed(2)} pagato`}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs sm:text-sm text-muted-foreground truncate ${isCancelled ? 'line-through' : ''}`}>{apt.customer_name || 'Cliente'}</p>
                        <p className="text-[11px] sm:text-xs text-muted-foreground flex items-center gap-1.5 mt-1 truncate">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span className="hidden sm:inline">{format(parseISO(apt.start_time), 'EEEE d MMM, HH:mm', { locale: it })}</span>
                          <span className="sm:hidden">{format(parseISO(apt.start_time), 'd MMM HH:mm', { locale: it })}</span>
                          <span className="text-primary truncate hidden sm:inline">• {apt.staff_name}</span>
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="flex-shrink-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(apt)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Modifica
                        </DropdownMenuItem>
                        {apt.appointment_status === 'cancelled' ? (
                          <DropdownMenuItem
                            onClick={() => handleRestore(apt.id)}
                            data-testid={`restore-appt-${apt.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Riattiva
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleCancel(apt.id)}
                            data-testid={`cancel-appt-${apt.id}`}
                            className="text-orange-600 focus:text-orange-700"
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Cancella
                          </DropdownMenuItem>
                        )}
                        {!isCancelled && apt.appointment_status !== 'no_show' && (
                          <DropdownMenuItem
                            onClick={() => handleNoShow(apt.id)}
                            data-testid={`noshow-appt-${apt.id}`}
                            className="text-amber-600 focus:text-amber-700"
                          >
                            <UserX className="w-4 h-4 mr-2" />
                            Non si è presentato
                          </DropdownMenuItem>
                        )}
                        {apt.deposit_paid && apt.deposit_outcome !== 'refunded' && (
                          <DropdownMenuItem
                            onClick={() => handleRefundDeposit(apt.id)}
                            data-testid={`refund-deposit-${apt.id}`}
                          >
                            <Undo2 className="w-4 h-4 mr-2" />
                            Restituisci acconto
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(apt.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Nessun appuntamento in programma</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Slot Broadcast Dialog */}
      <SlotBroadcastDialog
        open={broadcastDialogOpen}
        onOpenChange={(open) => {
          setBroadcastDialogOpen(open);
          if (!open) setBroadcastSlot(null);
        }}
        slotDateTime={broadcastSlot?.datetime || formData.start_time}
        slotDuration={
          broadcastSlot?.duration_min
            ?? (formData.start_time && formData.end_time
              ? Math.round((new Date(formData.end_time) - new Date(formData.start_time)) / 60000)
              : 30)
        }
        serviceName={broadcastSlot?.service_name || services.find(s => s.id === formData.service_id)?.name}
        appointmentId={broadcastSlot?.appointment_id || selectedAppointment?.id}
        onSent={onBroadcastSent}
      />
    </div>
  );
};

export default CalendarPage;
